import { FileText } from 'lucide-react'
import { defineModule } from '@/shell/module-system'
import { QuoteDetailPage } from './pages/QuoteDetailPage'
import { QuotesPage } from './pages/QuotesPage'

/**
 * Sales Quoting (Sprint 12 — Phase 2 capstone).
 *
 * Middleware-resident quote lifecycle for SPI's sales team. Outside
 * sales reps don't have Odoo access; the entire draft → submit →
 * review → approve → send loop runs from this module. SO creation
 * is PO-gated and explicit (D-1).
 *
 * RBAC: visibility for admin, sales, finance. Writes are gated
 * server-side per-endpoint (sales authors; finance + admin approve;
 * sales + admin record PO + convert to SO).
 */
export default defineModule({
  id: 'quotes',
  name: 'Quotes',
  shortName: 'Quotes',
  icon: FileText,
  // Sprint 13a (2026-04-26): moved from `ops` to `sales` — outside
  // sales reps run the full quote lifecycle, so it sits with the
  // other sales-persona surfaces. Finance still sees Quotes for
  // margin-approval review via the module's `permissions` array.
  navSection: 'sales',
  // Last in Sales (after open-orders, margins, fulfillment-risk,
  // customer-pricelists). Quotes is a discovery-style entry compared
  // to the live-data surfaces above it.
  navOrder: 50,
  urlNamespace: '/quotes',
  routes: [
    { path: '/', element: QuotesPage, label: 'Quotes' },
    // Static `/new` registered BEFORE `/$quoteId` so it doesn't get
    // captured as quoteId="new" by the dynamic param.
    { path: '/new', element: QuoteDetailPage, label: 'New quote' },
    { path: '/$quoteId', element: QuoteDetailPage, label: 'Quote' },
  ],
  permissions: [
    'admin', 'sales', 'outside_sales_rep', 'inside_sales_rep', 'finance',
  ],
  densityDefault: 'comfortable',
  filterRailDefault: 'collapsed',
  mobileStrategy: 'desktop-only',
  // 'g j' is taken by Projects, 'g r' by formulations, 'g p' by
  // procurement. 'g q' is the cleanest mnemonic for Quotes.
  keyboardShortcut: 'g q',
  commandPaletteEntries: [
    {
      label: 'Go to Quotes',
      keywords: ['quote', 'quotes', 'sales', 'pricing', 'rfq', 'proposal'],
      path: '/',
      group: 'modules',
      shortcut: 'g q',
    },
  ],
})
